import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config.js';

const DEFAULT_AVATAR = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="120" viewBox="0 0 100 120">
    <rect width="100" height="120" rx="8" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="2"/>
    <circle cx="50" cy="42" r="22" fill="#94a3b8"/>
    <ellipse cx="50" cy="100" rx="34" ry="24" fill="#94a3b8"/>
  </svg>
`);

const InstitutionHeader = ({ headerConfig = {}, branding = {}, orgName = '', orgAddress = '', primaryColor = '#1e3a8a', studentPhoto = '', photoPos = 'top-right' }) => {
  const cfg = headerConfig || {};
  const layout = cfg.layout || 'centered';
  const title = cfg.title || 'PROGRESS REPORT';
  const BASE_URL = API_BASE_URL || 'http://localhost:9980';
  const trustName = branding.trustName || cfg.trustName || '';

  const defaultLogo = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><circle cx="40" cy="40" r="38" fill="' + primaryColor + '" stroke="#f59e0b" stroke-width="3"/><text x="40" y="48" text-anchor="middle" font-size="28" font-weight="bold" fill="white" font-family="serif">S</text></svg>');
  
  const rawLogo = branding.logoUrl
    ? (branding.logoUrl.startsWith('http') || branding.logoUrl.startsWith('data:') ? branding.logoUrl : `${BASE_URL}${branding.logoUrl.startsWith('/') ? '' : '/'}${branding.logoUrl}`)
    : defaultLogo;

  const [logoSrc, setLogoSrc] = useState(rawLogo);
  const [photoSrc, setPhotoSrc] = useState(studentPhoto || DEFAULT_AVATAR);

  useEffect(() => {
    setLogoSrc(rawLogo);
  }, [rawLogo]);

  useEffect(() => {
    setPhotoSrc(studentPhoto || DEFAULT_AVATAR);
  }, [studentPhoto]);

  // ── Layout 1: Vibrant Gradient Banner ──
  if (layout === 'banner') {
    return (
      <div style={{ background: `linear-gradient(135deg, ${primaryColor}, #0284c7)`, padding: '16px 20px', borderRadius: '10px', color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {cfg.showLogo !== false && (
          <img
            src={logoSrc}
            alt=""
            onError={() => setLogoSrc(defaultLogo)}
            style={{ width: '65px', height: '65px', objectFit: 'contain' }}
          />
        )}
        <div style={{ textAlign: 'center', flex: 1 }}>
          {trustName && (
            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', opacity: 0.9, marginBottom: '2px' }}>
              {trustName}
            </div>
          )}
          {cfg.showSchoolName !== false && <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', textTransform: 'uppercase' }}>{orgName}</h1>}
          {cfg.showAddress !== false && <div style={{ fontSize: '10px', opacity: 0.9 }}>{orgAddress}</div>}
          <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '800', background: 'rgba(255,255,255,0.2)', display: 'inline-block', padding: '3px 12px', borderRadius: '20px' }}>
            {title}
          </div>
        </div>
        {photoPos !== 'none' && (
          <img
            src={photoSrc}
            alt=""
            onError={() => setPhotoSrc(DEFAULT_AVATAR)}
            style={{ width: '60px', height: '75px', borderRadius: '6px', border: '2px solid #fff', objectFit: 'cover', backgroundColor: '#f1f5f9' }}
          />
        )}
      </div>
    );
  }

  // ── Layout 2: Compact Minimal Certificate ──
  if (layout === 'compact') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1.5px solid #cbd5e1', paddingBottom: '8px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          {cfg.showLogo !== false && (
            <img
              src={logoSrc}
              alt=""
              onError={() => setLogoSrc(defaultLogo)}
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          )}
          <div>
            {trustName && (
              <div style={{ fontSize: '9.5px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {trustName}
              </div>
            )}
            {cfg.showSchoolName !== false && (
              <h2 style={{ margin: '1px 0', fontSize: '17px', fontWeight: '800', color: primaryColor, textTransform: 'uppercase' }}>
                {orgName}
              </h2>
            )}
            <div style={{ fontSize: '9.5px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {cfg.showAddress !== false && orgAddress && <span>{orgAddress}</span>}
              {cfg.showAffiliationNo !== false && branding.affiliationNo && <span>• Affiliation: {branding.affiliationNo}</span>}
              {cfg.showContact !== false && branding.phone && <span>• Ph: {branding.phone}</span>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ display: 'inline-block', border: `1.5px solid ${primaryColor}`, color: primaryColor, padding: '3px 12px', borderRadius: '4px', fontSize: '10.5px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>
              {title}
            </span>
          </div>
          {photoPos !== 'none' && (
            <img
              src={photoSrc}
              alt=""
              onError={() => setPhotoSrc(DEFAULT_AVATAR)}
              style={{ width: '50px', height: '62px', borderRadius: '4px', border: `1.5px solid #cbd5e1`, objectFit: 'cover', backgroundColor: '#f1f5f9' }}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Layout 3: Logo Left + Title Right ──
  if (layout === 'logo_left') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2.5px solid ${primaryColor}`, paddingBottom: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          {cfg.showLogo !== false && (
            <img
              src={logoSrc}
              alt=""
              onError={() => setLogoSrc(defaultLogo)}
              style={{ width: '75px', height: '75px', objectFit: 'contain' }}
            />
          )}
          <div>
            {trustName && (
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '1px' }}>
                {trustName}
              </div>
            )}
            {cfg.showSchoolName !== false && (
              <h1 style={{ margin: 0, fontSize: '21px', fontWeight: '800', color: primaryColor, textTransform: 'uppercase' }}>
                {orgName}
              </h1>
            )}
            {cfg.showAffiliationNo !== false && branding.affiliationNo && (
              <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>
                {branding.affiliationNo}
              </div>
            )}
            {cfg.showAddress !== false && orgAddress && (
              <div style={{ fontSize: '10px', color: '#475569', marginTop: '1px' }}>
                {orgAddress}
              </div>
            )}
            {cfg.showContact !== false && (branding.email || branding.phone) && (
              <div style={{ fontSize: '9px', color: '#64748b', marginTop: '1px' }}>
                {branding.email ? `Email: ${branding.email}` : ''}{branding.email && branding.phone ? ' | ' : ''}{branding.phone ? `Phone: ${branding.phone}` : ''}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {photoPos !== 'none' && (
            <img
              src={photoSrc}
              alt=""
              onError={() => setPhotoSrc(DEFAULT_AVATAR)}
              style={{ width: '65px', height: '80px', borderRadius: '6px', border: `2px solid ${primaryColor}`, objectFit: 'cover', backgroundColor: '#f1f5f9' }}
            />
          )}
          <div style={{ padding: '4px 14px', background: primaryColor, color: '#fff', fontSize: '11px', fontWeight: '700', borderRadius: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>
            {title}
          </div>
        </div>
      </div>
    );
  }

  // ── Layout 4: Centered Institutional Header (Default) ──
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `3px double ${primaryColor}`, paddingBottom: '14px', marginBottom: '16px' }}>
      {cfg.showLogo !== false && (
        <img
          src={logoSrc}
          alt=""
          onError={() => setLogoSrc(defaultLogo)}
          style={{ width: '75px', height: '75px', objectFit: 'contain' }}
        />
      )}
      
      <div style={{ textAlign: 'center', flex: 1, padding: '0 12px' }}>
        {trustName && (
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '2px' }}>
            {trustName}
          </div>
        )}
        {cfg.showSchoolName !== false && (
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: primaryColor, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            {orgName}
          </h1>
        )}
        {cfg.showAffiliationNo !== false && branding.affiliationNo && (
          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>
            {branding.affiliationNo}
          </div>
        )}
        {cfg.showAddress !== false && (
          <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px' }}>
            {orgAddress}
          </div>
        )}
        {cfg.showContact !== false && (branding.email || branding.phone) && (
          <div style={{ fontSize: '9px', color: '#64748b', marginTop: '1px' }}>
            {branding.email ? `Email: ${branding.email}` : ''}{branding.email && branding.phone ? ' | ' : ''}{branding.phone ? `Phone: ${branding.phone}` : ''}
          </div>
        )}
        <div style={{ marginTop: '8px', display: 'inline-block', padding: '4px 16px', background: primaryColor, color: '#fff', fontSize: '11px', fontWeight: '700', borderRadius: '4px', letterSpacing: '1px' }}>
          {title}
        </div>
      </div>

      {photoPos !== 'none' && (
        <img
          src={photoSrc}
          alt=""
          onError={() => setPhotoSrc(DEFAULT_AVATAR)}
          style={{ width: '65px', height: '80px', borderRadius: '6px', border: `2px solid ${primaryColor}`, objectFit: 'cover', backgroundColor: '#f1f5f9' }}
        />
      )}
    </div>
  );
};

export default InstitutionHeader;
